import { useCallback, useEffect, useRef, useState } from "react";

import { SearchNotFoundIcon } from "components/icons";
import Pagination from "./Pagination";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import { TPagination, TSort, TTabeField, TTableRecord } from "./types";
import { DEFAULT_TABLE_HEIGHT_VH } from "./constants";

import styles from "./Table.module.css";

export interface TableProps {
  keyId: string;
  containerClassName?: string;
  height?: number; //vh unit
  records: TTableRecord[];
  fields: TTabeField[];
  hasCheckbox?: boolean;
  pagination?: TPagination;
  selectedRows?: string[];
  loading?: boolean;
  hasFilter?: boolean;
  isSelectedAllPage?: boolean;
  fixedHeight?: boolean;
  emptyLabel?: string;
  emptyIcon?: React.ReactNode;
  classNameEmptyIconContainer?: string;
  onChangePage?: (page: number) => void;
  onChangePerPage?: (perPage: number) => void;
  onSort?: (sort: TSort) => void;
  onSelectAllCurrentPage?: (checked: boolean) => void;
  onSelectAllPage?: (isSelectAll: boolean) => void;
  onRowChecked?: (selectedIds: string[]) => void;
  onRowClick?: (id: string) => void;
}

const Table = ({
  keyId,
  containerClassName,
  height,
  records,
  fields,
  hasCheckbox,
  pagination,
  selectedRows = [],
  loading,
  isSelectedAllPage,
  fixedHeight,
  emptyLabel,
  emptyIcon,
  hasFilter,
  classNameEmptyIconContainer,
  onChangePage,
  onChangePerPage,
  onSort,
  onSelectAllCurrentPage,
  onSelectAllPage,
  onRowChecked,
  onRowClick,
}: TableProps) => {
  const { t } = useTranslationContext();
  const rendered = useRef(false);
  const [_loading, setLoading] = useState(true);
  const handleOnSelectRow = (id: string, checked: boolean) => {
    if (onRowChecked) {
      if (!checked) {
        onRowChecked(selectedRows.filter((rowId) => rowId !== id));
      } else {
        onRowChecked([...selectedRows, id]);
      }
    }
  };

  const handleRowClick = useCallback(
    (id: string) => {
      onRowClick?.(id);
    },
    [onRowClick]
  );

  useEffect(() => {
    if (rendered.current) {
      setLoading(!!loading);
    }

    const timeout = setTimeout(() => {
      if (!rendered.current && !loading) {
        setLoading(false);
        rendered.current = true;
      }
    }, 800);

    return () => {
      clearTimeout(timeout);
    };
  }, [loading, records]);

  return (
    <div
      className={cn(
        [styles.container],
        "relative",
        containerClassName && "-mb-[2rem]"
      )}
      style={
        containerClassName
          ? undefined
          : {
              minHeight: `${height ? height : DEFAULT_TABLE_HEIGHT_VH}vh`,
            }
      }
    >
      <div className="w-full">
        <div className="relative">
          <TableHeader
            fields={fields}
            hasCheckbox={hasCheckbox}
            selectedRows={selectedRows}
            isSelectedAllPage={
              isSelectedAllPage ||
              (!!records.length &&
                selectedRows.length === pagination?.totalItem)
            }
            pagination={pagination}
            loading={_loading}
            totalRecord={records?.length}
            onSelectAllCurrentPage={onSelectAllCurrentPage}
            onSelectAllPage={onSelectAllPage}
            onSort={onSort}
          />
        </div>

        <div
          className={cn(
            fixedHeight
              ? "overflow-y-auto pb-[3.7rem] bg-white"
              : "overflow-y-hidden",
            styles.scrollBar,
            fixedHeight ? containerClassName : ""
          )}
          style={
            containerClassName
              ? undefined
              : fixedHeight
                ? {
                    minHeight: `${
                      (height ? height : DEFAULT_TABLE_HEIGHT_VH) - 12
                    }vh`,
                  }
                : {}
          }
        >
          {records.map((record, index) => (
            <TableRow
              key={index}
              fields={fields}
              record={record}
              keyId={keyId}
              hasCheckbox={hasCheckbox}
              isSelected={
                selectedRows.includes(record?.[keyId]) || isSelectedAllPage
              }
              loading={_loading}
              position={index}
              onClick={handleRowClick}
              onSelect={handleOnSelectRow}
            />
          ))}

          {!records.length && (
            <div className="flex flex-col justify-center items-center h-full py-2">
              {_loading ? (
                <div className="w-full flex flex-col justify-center items-center h-full">
                  {t("loading")}
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center gap-3">
                  <div
                    className={cn(
                      "flex p-6 min-h-[5.5rem] min-w-[5.5rem] text-primary-500 bg-primary-50 justify-center items-center rounded-full",
                      classNameEmptyIconContainer
                    )}
                  >
                    <>
                      {hasFilter || !emptyIcon ? (
                        <SearchNotFoundIcon width={55} height={55} />
                      ) : (
                        emptyIcon
                      )}
                    </>
                  </div>
                  <p className="text-base text-center text-gray-800">
                    {hasFilter
                      ? t("no_result_found")
                      : emptyLabel
                        ? emptyLabel
                        : t("no_data_available")}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!!pagination && pagination.totalItem > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          itemPerPage={pagination.itemPerPage}
          totalItem={pagination.totalItem}
          perPageOptions={pagination?.perPageOptions}
          onChangePage={onChangePage}
          onChangePerPage={onChangePerPage}
        />
      )}
    </div>
  );
};

export default Table;
